import {
  MeshBuilder,
  StandardMaterial,
  Color3,
  type Scene,
  type Mesh
} from '@babylonjs/core';

export interface TerrainGridOptions {
  size?: number;
  subdivisions?: number;
  groundColor?: Color3;
}

export class TerrainGrid {
  public readonly size: number;
  public readonly halfSize: number;
  public readonly subdivisions: number;
  private groundColor: Color3;
  private groundMesh: Mesh | null = null;

  constructor(options: TerrainGridOptions = {}) {
    this.size = options.size ?? 60;
    this.halfSize = this.size / 2;
    this.subdivisions = options.subdivisions ?? 30;
    this.groundColor = options.groundColor ?? new Color3(0.08, 0.12, 0.18);
  }

  public isInsideBounds(x: number, z: number): boolean {
    return Math.abs(x) <= this.halfSize && Math.abs(z) <= this.halfSize;
  }

  public snapToGrid(x: number, z: number, cellSize: number = 1.0): { x: number; z: number } {
    return {
      x: Math.round(x / cellSize) * cellSize,
      z: Math.round(z / cellSize) * cellSize
    };
  }

  public getElevationAt(_x: number, _z: number): number {
    // Baseline flat terrain at y = 0, ready for future heightmap/noise extensions
    return 0.0;
  }

  public createMesh(scene: Scene): Mesh {
    const ground = MeshBuilder.CreateGround(
      'terrainGround',
      {
        width: this.size,
        height: this.size,
        subdivisions: this.subdivisions
      },
      scene
    );

    const mat = new StandardMaterial('terrainGroundMat', scene);
    mat.diffuseColor = this.groundColor;
    mat.specularColor = new Color3(0.03, 0.04, 0.05);
    mat.roughness = 0.9;

    ground.material = mat;
    ground.receiveShadows = true;
    ground.checkCollisions = true;

    this.groundMesh = ground;
    return ground;
  }

  public getMesh(): Mesh | null {
    return this.groundMesh;
  }
}
